"use client";

import { domAnimation, LazyMotion, m } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect } from "react";
import type { Blog } from "@/app/api/blog/route";
import { BlogNewsletterForm } from "@/components/blog/newsletter_form";
import { MarkdownRenderer } from "@/components/shared/markdown_renderer";
import { clearTransition } from "@/lib/stores/blog-transition-store";

interface BlogPostClientProps {
	blog: Blog;
	relatedPosts: Blog[];
}

const fadeUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
};

function RelatedPosts({ relatedPosts }: { relatedPosts: Blog[] }) {
	if (relatedPosts.length === 0) return null;

	return (
		<section className="py-20 bg-zinc-950">
			<div className="container mx-auto px-4">
				<m.h2
					className="font-syne text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-12"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}
				>
					Related <span className="text-red-500">Articles</span>
				</m.h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
					{relatedPosts.map((post, index) => (
						<m.div
							key={post.id}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: index * 0.1, duration: 0.5 }}
						>
							<Link href={`/blog/${post.slug}`} className="group h-full block">
								<article className="h-full bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden hover:shadow-xl hover:shadow-red-950/20 hover:border-zinc-700 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 flex flex-col">
									<div className="aspect-[16/10] w-full overflow-hidden bg-zinc-800 relative">
										<Image
											src={post.coverImage || "/placeholders/blog-cover.svg"}
											alt={post.title}
											width={640}
											height={400}
											className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
											loading="lazy"
											sizes="(max-width: 768px) 100vw, 33vw"
										/>
										<div className="absolute top-4 left-4 flex flex-wrap gap-2">
											{post.tags.slice(0, 2).map((tag) => (
												<span
													key={tag}
													className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-xs font-bold font-syne uppercase tracking-wide text-white/80 border border-white/10"
												>
													{tag}
												</span>
											))}
										</div>
									</div>
									<div className="p-6 flex flex-col flex-1">
										<div className="flex items-center text-xs font-medium text-zinc-500 mb-3 space-x-2 font-inter uppercase tracking-wider">
											<span>{post.readTime}</span>
											<span>&bull;</span>
											<span>{post.date}</span>
										</div>
										<h3 className="font-syne text-lg font-bold uppercase tracking-tight text-white mb-3 group-hover:text-red-400 transition-colors line-clamp-2">
											{post.title}
										</h3>
										<p className="font-inter text-zinc-500 text-sm line-clamp-2 leading-relaxed">
											{post.excerpt}
										</p>
										<div className="mt-auto pt-4 flex items-center justify-end">
											<span className="text-red-500 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-[opacity,transform] duration-300 flex items-center text-sm font-syne font-bold uppercase tracking-wide">
												Read
												<ArrowRight className="w-4 h-4 ml-1" />
											</span>
										</div>
									</div>
								</article>
							</Link>
						</m.div>
					))}
				</div>
			</div>
		</section>
	);
}

export function BlogPostClient({ blog, relatedPosts }: BlogPostClientProps) {
	// Clear the morph transition overlay on mount
	useEffect(() => {
		const timer = setTimeout(() => {
			clearTransition();
		}, 100);
		return () => clearTimeout(timer);
	}, []);

	const handleCopyLink = useCallback(() => {
		if (typeof window !== "undefined") {
			navigator.clipboard.writeText(window.location.href);
		}
	}, []);

	const handleShareFacebook = useCallback(() => {
		if (typeof window !== "undefined") {
			window.open(
				`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
				"_blank",
				"width=600,height=400",
			);
		}
	}, []);

	const handleShareTwitter = useCallback(() => {
		if (typeof window !== "undefined") {
			window.open(
				`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`,
				"_blank",
				"width=600,height=400",
			);
		}
	}, [blog.title]);

	const handleShareLinkedIn = useCallback(() => {
		if (typeof window !== "undefined") {
			window.open(
				`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
				"_blank",
				"width=600,height=400",
			);
		}
	}, []);

	return (
		<LazyMotion features={domAnimation}>
			<div className="blog-post-page bg-white overflow-x-hidden">
				{/* Cinematic Hero Section */}
				<section className="relative min-h-[70vh] flex items-end overflow-hidden bg-zinc-950 -mt-20">
					{/* Cover image as background */}
					<div className="absolute inset-0">
						<Image
							src={blog.coverImage || "/placeholders/blog-cover.svg"}
							alt={blog.title}
							fill
							priority
							className="object-cover blur-[6px] scale-[1.02]"
							sizes="100vw"
						/>
						{/* Gradient overlays for readability */}
						<div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/30" />
						<div className="absolute inset-0 bg-gradient-to-r from-zinc-950/40 to-transparent" />
					</div>

					{/* Noise overlay */}
					<div className="absolute inset-0 bg-[url(/noise.svg)] opacity-10 mix-blend-overlay pointer-events-none" />

					{/* Hero content */}
					<div className="container mx-auto px-4 relative z-10 pb-16 pt-32">
						<m.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
							<Link
								href="/blog"
								className="inline-flex items-center text-red-400 hover:text-red-300 transition-colors font-syne font-bold uppercase tracking-wide text-sm mb-8"
							>
								<ArrowLeft className="w-4 h-4 mr-2" />
								Back to Journal
							</Link>
						</m.div>

						<m.div
							className="flex flex-wrap gap-2 mb-6"
							{...fadeUp}
							transition={{ duration: 0.5, delay: 0.2 }}
						>
							{blog.tags.map((tag) => (
								<Link href="/blog" key={tag}>
									<span className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-lg text-xs font-bold font-syne uppercase tracking-wide text-white/80 border border-white/10 hover:bg-white/20 transition-colors">
										{tag}
									</span>
								</Link>
							))}
						</m.div>

						<m.h1
							className="font-syne text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-white leading-[0.95] max-w-5xl mb-8"
							{...fadeUp}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							{blog.title}
						</m.h1>

						<m.div
							className="flex flex-wrap items-center gap-x-6 gap-y-3 text-zinc-400"
							{...fadeUp}
							transition={{ duration: 0.5, delay: 0.4 }}
						>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 overflow-hidden mr-3">
									<Image
										src={blog.author.avatar || "/placeholders/avatar.svg"}
										alt={blog.author.name}
										width={40}
										height={40}
										className="w-full h-full object-cover"
									/>
								</div>
								<span className="font-syne font-bold uppercase tracking-wide text-sm text-white">
									{blog.author.name}
								</span>
							</div>
							<div className="flex items-center text-sm font-inter">
								<Calendar className="w-4 h-4 mr-2 text-zinc-500" />
								<span>{blog.date}</span>
							</div>
							<div className="flex items-center text-sm font-inter">
								<Clock className="w-4 h-4 mr-2 text-zinc-500" />
								<span>{blog.readTime}</span>
							</div>
						</m.div>
					</div>
				</section>

				{/* Blog Content */}
				<m.section
					className="py-16 bg-white"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, delay: 0.5 }}
				>
					<div className="container mx-auto px-4">
						<div className="max-w-3xl mx-auto">
							<div className="blog-content">
								<MarkdownRenderer content={blog.content} variant="public" />
							</div>
						</div>
					</div>
				</m.section>

				{/* Share Section */}
				<section className="py-12 bg-zinc-950 border-t border-zinc-800">
					<div className="container mx-auto px-4">
						<div className="max-w-3xl mx-auto">
							<h3 className="font-syne text-lg font-bold uppercase tracking-wide text-white mb-4">
								Share This Article
							</h3>
							<div className="flex flex-wrap items-center gap-3">
								<button
									type="button"
									className="size-10 inline-flex items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-red-600 hover:bg-red-600/10 transition-colors"
									onClick={handleShareFacebook}
									aria-label="Share on Facebook"
								>
									<svg
										aria-hidden="true"
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
									</svg>
								</button>
								<button
									type="button"
									className="size-10 inline-flex items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-red-600 hover:bg-red-600/10 transition-colors"
									onClick={handleShareTwitter}
									aria-label="Share on Twitter"
								>
									<svg
										aria-hidden="true"
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
									</svg>
								</button>
								<button
									type="button"
									className="size-10 inline-flex items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-red-600 hover:bg-red-600/10 transition-colors"
									onClick={handleShareLinkedIn}
									aria-label="Share on LinkedIn"
								>
									<svg
										aria-hidden="true"
										xmlns="http://www.w3.org/2000/svg"
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
										<rect x="2" y="9" width="4" height="12" />
										<circle cx="4" cy="4" r="2" />
									</svg>
								</button>
								<span className="mx-1 h-6 w-px bg-zinc-800" />
								<button
									type="button"
									className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-red-600 hover:bg-red-600/10 transition-colors font-syne uppercase tracking-wide text-sm"
									onClick={handleCopyLink}
								>
									<Share2 className="w-4 h-4" />
									Copy Link
								</button>
							</div>
						</div>
					</div>
				</section>

				<RelatedPosts relatedPosts={relatedPosts} />

				{/* Newsletter Section */}
				<section className="py-24 bg-zinc-900 text-white relative overflow-hidden">
					<div className="absolute inset-0 bg-[url(/noise.svg)] opacity-20 mix-blend-overlay pointer-events-none" />
					<div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />

					<div className="container mx-auto px-4 relative z-10">
						<div className="max-w-4xl mx-auto text-center">
							<h2 className="font-syne text-4xl md:text-5xl font-bold uppercase tracking-tight mb-6">
								Stay <span className="text-primary">Ahead</span>
							</h2>
							<p className="font-inter text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
								Enjoyed this article? Subscribe to get the latest creator
								strategies, campaign tips, and industry updates delivered
								straight to your inbox.
							</p>

							<BlogNewsletterForm source={`blog post: ${blog.slug}`} />
							<p className="mt-6 text-xs text-zinc-500 font-inter">
								No spam. Unsubscribe anytime.
							</p>
						</div>
					</div>
				</section>
			</div>
		</LazyMotion>
	);
}
